import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { FileDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { readUpload, saveBlob, sizeLabel } from '../files';
import { fmtDate } from '../format';
import { Empty, ErrorBanner, Modal, Panel } from '../ui';
import { ask } from '../dialogs';

type Tab = 'client' | 'shared';

/**
 * Documenti.
 * - "Per cliente": file condivisi con un cliente (contratti, materiali, file di lavoro).
 * - "Questionari e modelli": file di lavoro dello staff (es. questionari sicurezza da far compilare),
 *   pronti da allegare a una mail dalla Posta.
 */
export function DocumentsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const role = ctx.me.user.role;
  const isClient = role === 'client';
  const param = (ctx.pageParam ?? {}) as { tab?: Tab };
  const [tab, setTab] = useState<Tab>(isClient ? 'client' : (param.tab ?? 'client'));
  const [clients, setClients] = useState<{ id: number; name: string }[]>([]);
  const [clientId, setClientId] = useState<number | null>(null);
  const [files, setFiles] = useState<FileDto[] | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<FileDto | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const shared = tab === 'shared';

  useEffect(() => {
    if (isClient) return;
    api
      .get<{ id: number; name: string }[]>('/api/quotes/meta/clients')
      .then((c) => {
        setClients(c);
        setClientId((cur) => cur ?? c[0]?.id ?? null);
      })
      .catch((e) => setError((e as Error).message));
  }, [api, isClient]);

  const load = useCallback(async () => {
    if (shared) {
      setFiles(await api.get<FileDto[]>('/api/files/shared').catch((e) => (setError((e as Error).message), null)));
      return;
    }
    if (!isClient && clientId == null) return;
    try {
      setFiles(await api.get<FileDto[]>(`/api/files/documents${isClient ? '' : `?clientId=${clientId}`}`));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, isClient, clientId, shared]);

  useEffect(() => {
    setFiles(null);
    void load();
  }, [load]);

  const upload = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of [...list]) {
        const u = await readUpload(f);
        const body = { name: u.name, mime: u.mime, contentBase64: u.contentBase64, note: note.trim() || null };
        if (shared) await api.post('/api/files/shared', body);
        else await api.post('/api/files/documents', { ...body, clientId: isClient ? undefined : clientId });
      }
      setNote('');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const download = async (f: FileDto) => {
    try {
      saveBlob(await api.download(`/api/files/${f.id}/download`), f.name);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async (f: FileDto) => {
    if (!(await ask(`Eliminare "${f.name}"?`))) return;
    try {
      await api.del(`/api/files/documents/${f.id}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Il commerciale ha la Posta: apre un nuovo messaggio con il file già allegato
  const sendByMail = (f: FileDto) => ctx.setPage('mail', { compose: { subject: f.name.replace(/\.[a-z0-9]+$/i, '').replace(/_/g, ' '), files: [f] } });

  const canDelete = (f: FileDto) => role === 'admin' || f.uploadedBy === ctx.me.user.id;
  const subtitle = isClient ? 'File condivisi con il tuo consulente' : shared ? 'Questionari, moduli e materiali pronti da inviare ai clienti' : 'File condivisi con il cliente';

  return (
    <div className="panels single">
      <Panel
        title="Documenti"
        subtitle={subtitle}
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            {!isClient && (
              <div className="doc-tabs">
                <button type="button" className={`btn small${!shared ? ' dark' : ''}`} title="I file di un cliente: li vede anche lui nella sua area" onClick={() => setTab('client')}>
                  <Icon name="building" size={14} /> Per cliente
                </button>
                <button type="button" className={`btn small${shared ? ' dark' : ''}`} title="Moduli e materiali da riusare con tutti i clienti: i clienti non li vedono" onClick={() => setTab('shared')}>
                  <Icon name="list" size={14} /> Questionari e modelli
                </button>
              </div>
            )}
            {!isClient && !shared && (
              <select value={clientId ?? ''} title="Di quale cliente vedere i file" onChange={(e) => setClientId(Number(e.target.value))}>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            <input ref={input} type="file" multiple hidden onChange={(e) => void upload(e.target.files)} />
            <button
              type="button"
              className="btn dark"
              title={isClient ? 'Manda un file al tuo consulente' : shared ? 'Carica un modulo da riusare (i clienti non lo vedono)' : 'Carica un file per questo cliente: lo vedrà anche lui'}
              onClick={() => input.current?.click()}
              disabled={busy || (!isClient && !shared && clientId == null)}
            >
              <Icon name="plus" size={16} /> {busy ? 'Caricamento…' : 'Carica file'}
            </button>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <input className="deal-search" style={{ width: '100%', marginBottom: 12 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder={shared ? 'Nota per il prossimo file, es. "Per OMI Group, da far compilare" (facoltativa)' : 'Nota per il prossimo file caricato (facoltativa)'} maxLength={300} />
        {!files ? (
          <div className="muted small">{!isClient && !shared && clients.length === 0 ? 'Nessun cliente collegato.' : 'Caricamento…'}</div>
        ) : files.length === 0 ? (
          <Empty icon="inbox">{shared ? 'Nessun questionario o modello caricato.' : 'Nessun documento condiviso.'}</Empty>
        ) : (
          <div className="ag-list">
            {files.map((f) => (
              <div key={f.id} className="ag-row" style={{ cursor: 'default' }}>
                <Icon name="paperclip" size={15} />
                <span className="ag-title doc-open" onClick={() => setPreview(f)} title="Visualizza">
                  <b>{f.name}</b>
                  <div className="muted small">
                    {sizeLabel(f.size)} · {f.uploadedByName} · {fmtDate(f.createdAt)}
                    {f.note ? ` · ${f.note}` : ''}
                  </div>
                </span>
                {shared && !isClient && (
                  <button type="button" className="btn small" onClick={() => sendByMail(f)} title="Nuovo messaggio con questo file allegato">
                    <Icon name="send" size={14} /> Invia per mail
                  </button>
                )}
                <button type="button" className="btn ghost" onClick={() => setPreview(f)} title="Visualizza">
                  <Icon name="eye" size={15} />
                </button>
                <button type="button" className="btn ghost" onClick={() => void download(f)} title="Scarica">
                  <Icon name="download" size={15} />
                </button>
                {canDelete(f) && (
                  <button type="button" className="btn ghost" onClick={() => void remove(f)} title="Elimina">
                    <Icon name="trash" size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
      {preview && <FilePreview ctx={ctx} file={preview} onClose={() => setPreview(null)} onDownload={() => void download(preview)} />}
    </div>
  );
}

type Kind = 'pdf' | 'image' | 'text' | 'other';
const kindOf = (f: FileDto): Kind => {
  const n = f.name.toLowerCase();
  if (f.mime === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
  if (f.mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(n)) return 'image';
  if (f.mime.startsWith('text/') || /\.(txt|csv|md|log|json|xml)$/.test(n)) return 'text';
  return 'other';
};
const MIME: Record<Kind, string> = { pdf: 'application/pdf', image: '', text: 'text/plain;charset=utf-8', other: '' };

/** Anteprima di un documento senza salvarlo sul computer (PDF, immagini, testo). */
export function FilePreview({ ctx, file, onClose, onDownload }: { ctx: Ctx; file: FileDto; onClose: () => void; onDownload: () => void }) {
  const kind = kindOf(file);
  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (kind === 'other') return;
    let u: string | null = null;
    let alive = true;
    ctx.api
      .download(`/api/files/${file.id}/download`)
      .then(async (b) => {
        if (!alive) return;
        if (kind === 'text') return setText(await b.text());
        const typed = MIME[kind] ? new Blob([b], { type: MIME[kind] }) : b;
        u = URL.createObjectURL(typed);
        setUrl(u);
      })
      .catch((e) => alive && setError((e as Error).message));
    return () => {
      alive = false;
      if (u) URL.revokeObjectURL(u);
    };
  }, [ctx.api, file.id, kind]);

  return (
    <Modal
      title={file.name}
      onClose={onClose}
      resizable
      footer={
        <div className="quote-actions">
          <button type="button" className="btn" onClick={onClose}>
            Chiudi
          </button>
          <button type="button" className="btn dark" onClick={onDownload}>
            <Icon name="download" size={15} /> Scarica
          </button>
        </div>
      }
    >
      <div className="doc-preview">
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {kind === 'other' ? (
          <Empty icon="inbox">Anteprima non disponibile: usa «Scarica» per aprirlo.</Empty>
        ) : !url && text == null && !error ? (
          <div className="muted small">Caricamento…</div>
        ) : kind === 'pdf' && url ? (
          <iframe src={url} title={file.name} />
        ) : kind === 'image' && url ? (
          <img src={url} alt={file.name} />
        ) : text != null ? (
          <pre>{text}</pre>
        ) : null}
      </div>
    </Modal>
  );
}
